/**
 * HTML sanitization helper used before any `dangerouslySetInnerHTML`.
 *
 * GearLens stores HTML for site-wide / org-wide banners (rendered via
 * `marked.parse()` from admin-supplied markdown). Even though only privileged
 * users can edit banners, we sanitize defensively to:
 *   - prevent stored XSS if an admin account is compromised,
 *   - prevent self-XSS / mistake-driven script injection from copy-pasted
 *     HTML in the markdown editor,
 *   - guard against future code paths feeding less-trusted HTML through the
 *     same render components.
 *
 * Uses `sanitize-html` (isomorphic, no DOM dependency) so it works in both
 * SSR (server middleware / route handlers) and browser bundles.
 */
import sanitizeHtml from 'sanitize-html'

/**
 * Conservative allow-list suitable for marketing/announcement banners:
 *   - inline formatting (bold, italic, code)
 *   - links (http(s)/mailto only, target=_blank rel forced)
 *   - lists, paragraphs, line breaks
 *   - simple block elements emitted by `marked` from markdown
 *
 * Disallows: <script>, <style>, <iframe>, <object>, <embed>, event handlers,
 * `javascript:` URLs, inline `style` attributes, `data:` URLs in links, etc.
 */
const BANNER_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    'p', 'br', 'span', 'div',
    'strong', 'b', 'em', 'i', 'u', 's', 'del', 'mark',
    'code', 'pre', 'kbd',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote',
    'a',
    'hr',
  ],
  allowedAttributes: {
    a: ['href', 'title', 'target', 'rel'],
    // Allow class for prose styling but no inline style/event handlers.
    '*': ['class'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  allowedSchemesAppliedToAttributes: ['href'],
  // Force safe link behaviour: external links open in new tab with
  // noopener/noreferrer to prevent reverse-tabnabbing.
  transformTags: {
    a: sanitizeHtml.simpleTransform('a', {
      target: '_blank',
      rel: 'noopener noreferrer',
    }),
  },
  // Drop the contents of these tags entirely (instead of leaking text).
  nonTextTags: ['style', 'script', 'textarea', 'option', 'noscript'],
}

/**
 * Sanitize HTML intended for banner rendering.
 *
 * Always returns a string; never throws. Empty/nullish input → "".
 */
export function sanitizeBannerHtml(html: string | null | undefined): string {
  if (!html) return ''
  return sanitizeHtml(html, BANNER_OPTIONS)
}
